import type {
  ChatSourceFssFile,
  ChatSourceFssFolder,
  ChatSourceInclusionType,
} from "@mirage/mosaics/Chat/types";
import type {
  FSSEntityWithFileId,
  FSSFile,
  FSSFolderWithFileId,
} from "@mirage/service-fss-bridge/service/bridge_types";

/**
 * Filter out folders that are not chat sources, e.g. FSSFolder for scoping
 * which is not for summarization and does not have a `fileId`.
 * @note using a generic here instead of FSSEntityWithFileId due to
 * typescript not narrowing down the type properly
 */
export const isFSSEntityWithFileId = <T extends { fileId?: string }>(
  entity: T,
): entity is T & { fileId: string } => {
  return Boolean(entity.fileId);
};

export const convertFssEntityToChatSource = (
  entity: FSSEntityWithFileId,
): ChatSourceFssFile | ChatSourceFssFolder => {
  if (entity.type === "file") {
    return convertFssFileToChatSource(entity);
  }
  return convertFssFolderToChatSource(entity);
};

export const convertFssEntityScopeToChatSource = (
  entity: FSSEntityWithFileId,
  inclusionType: ChatSourceInclusionType,
): ChatSourceFssFile | ChatSourceFssFolder => {
  if (entity.type === "file") {
    return convertFssFileToChatSource(entity, inclusionType);
  }
  return convertFssFolderToChatSource(entity, inclusionType);
};

export const parseFileExtension = (fileName: string) => {
  // split the file name into the name and the extension
  const fileNameParts = fileName.match(/^.+\.([^.]+)$/);
  return fileNameParts?.[1] ?? "";
};

export const convertFssFileToChatSource = (
  file: FSSFile,
  inclusionType?: ChatSourceInclusionType,
): ChatSourceFssFile => {
  const fullFileName = file.name;
  const extension = parseFileExtension(fullFileName);

  return {
    type: "fss_file",
    fileId: file.fileId,
    name: file.name,
    url: file.href ?? null,
    extension,
    inclusionType,
  };
};

export const convertFssFolderToChatSource = (
  folder: FSSFolderWithFileId,
  inclusionType?: ChatSourceInclusionType,
): ChatSourceFssFolder => {
  return {
    type: "fss_folder",
    fileId: folder.fileId,
    path: folder.path,
    name: folder.path.split("/").pop() ?? undefined,
    inclusionType,
  };
};
